/**
 * Mention syntax — the one place that knows what `[[…|…]]` means.
 *
 * A mention is stored the same way everywhere it can be written (page bodies, notes, the Y.Doc
 * markdown mirror): a double-bracketed pair of a REFERENCE and a LABEL, `[[ref|label]]`. The label is
 * what the reader sees; the reference is what the click resolves.
 *
 * Two kinds of reference share the one `@` trigger, told apart by a sigil:
 *
 *   Item     `[[6f0c…-…|Regenerative Finance]]`   — a bare id, exactly as before this module existed
 *   Account  `[[@2b7d…-…|Ana Ruiz]]`              — the same id shape behind a leading '@'
 *
 * The sigil, not the id shape, is the discriminator. Items and accounts are both uuids on Supabase
 * and both `rec…` strings on Airtable, so nothing about the id itself could separate them, and a
 * new kind gets its own sigil rather than a parsing heuristic. Three did (2026-10-19), each a word
 * and a colon — no item id holds a colon:
 *
 *   Type     `[[type:9b1e…|Organizations]]`  — opens the type in the sidebar
 *   Field    `[[field:41c2…|Email]]`          — opens the field
 *   Group    `[[group:77d0…|Stewards]]`       — opens the group's popup
 *
 * A reader never sees the name of something they may not see (decided 2026-10-19): what a mention
 * shows is asked of `mention_targets` (src/data/mentionTargets.js), and the label written here is
 * what search, the AI and exports read, not what a screen draws.
 *
 * Historical note: the reference pattern used to be written `[a-zA-Z0-9]+` at each call site, which
 * matched an Airtable `recXXXX` id and silently failed on every Supabase uuid (the hyphens). Anything
 * that needs the pattern imports MENTION_RE from here instead of spelling it again.
 */

/** Marks a reference as pointing at an account rather than an item. */
export const ACCOUNT_SIGIL = '@';

/** The kinds written as `<kind>:<id>`. */
export const PREFIXED_KINDS = Object.freeze(['type', 'field', 'group']);
const PREFIX_RE = /^(type|field|group):(.*)$/;

/** Every kind a mention can name. */
export const MENTION_KINDS = Object.freeze(['item', 'account', ...PREFIXED_KINDS]);

/**
 * Global matcher for `[[ref|label]]`. Group 1 is the raw reference, group 2 the label.
 *
 * The separator may arrive escaped, `[[ref\|label]]`: that is how the editor writes a mention inside a
 * table cell (and Obsidian's own convention there), because a bare pipe in a row is a column break.
 * The reference never holds a backslash, so without the optional `\` here the escaped form matched
 * with the backslash glued onto the id — a mention that rendered but resolved to nothing, and a guide
 * whose item read failed on an id that is not a uuid.
 */
export const MENTION_RE = /\[\[([^[\]|\\]+)\\?\|([^[\]]+)\]\]/g;

/**
 * Split a raw reference into its kind and id.
 * @param {string} rawRef the text between `[[` and `|`
 * @returns {{kind: 'account'|'item'|'type'|'field'|'group', id: string}|null} null when there is no usable id
 */
export function parseMentionRef(rawRef) {
  const ref = String(rawRef ?? '').trim();
  if (!ref) return null;
  if (ref.startsWith(ACCOUNT_SIGIL)) {
    const id = ref.slice(ACCOUNT_SIGIL.length).trim();
    return id ? { kind: 'account', id } : null;
  }
  const prefixed = ref.match(PREFIX_RE);
  if (prefixed) {
    const id = prefixed[2].trim();
    return id ? { kind: prefixed[1], id } : null;
  }
  return { kind: 'item', id: ref };
}

/** The stored reference for a target — the inverse of parseMentionRef. */
export function formatMentionRef(kind, id) {
  const clean = String(id ?? '').trim();
  if (!clean) return '';
  if (kind === 'account') return `${ACCOUNT_SIGIL}${clean}`;
  if (PREFIXED_KINDS.includes(kind)) return `${kind}:${clean}`;
  return clean;
}

/** What a hidden mention reads as, wherever it is drawn. */
export const HIDDEN_MENTION_TEXT = 'Something you can’t see';

/**
 * What one mention should draw, from its answer. Pure, so `node --test` pins it.
 * @param {{visible: boolean, label?: string|null, handle?: string|null}|undefined|null} answer
 *   undefined while the answer is being fetched; `{ visible: true, label: null }` when the stored
 *   label stands (an unchecked kind, a backend with no such rule, a failed call)
 * @param {string} storedLabel the label written in the text
 * @returns {{state: 'visible'|'hidden'|'pending', text: string, handle: string|null}}
 */
export function mentionDisplay(answer, storedLabel) {
  if (answer === undefined) return { state: 'pending', text: '…', handle: null };
  if (!answer || answer.visible === false) return { state: 'hidden', text: HIDDEN_MENTION_TEXT, handle: null };
  return { state: 'visible', text: answer.label || storedLabel || '', handle: answer.handle || null };
}

/**
 * Whether a mention's name depends on who is reading: everything but a person. A person's mention
 * keeps its own rules (who may be tagged on a page — useMentionAccessGate).
 */
export function isAudienceGatedKind(kind) {
  return kind === 'item' || PREFIXED_KINDS.includes(kind);
}

/** The full `[[ref|label]]` text for a target, ready to splice into markdown. */
export function serializeMention({ kind, id, label }) {
  const ref = formatMentionRef(kind, id);
  if (!ref) return '';
  // A label carrying the delimiters would round-trip as a different mention, so flatten them.
  const safeLabel = String(label ?? '').replace(/[[\]|]/g, ' ').trim() || ref;
  return `[[${ref}|${safeLabel}]]`;
}

/**
 * Every mention in a block of text, in document order.
 * @param {string} text
 * @returns {Array<{kind: 'account'|'item', id: string, label: string}>}
 */
export function extractMentions(text) {
  if (!text) return [];
  const out = [];
  // A module-level regex with /g carries lastIndex across calls — clone it per call so concurrent
  // callers (a save and a render in the same tick) cannot eat each other's matches.
  const re = new RegExp(MENTION_RE.source, 'g');
  let m = re.exec(text);
  while (m) {
    const target = parseMentionRef(m[1]);
    if (target) out.push({ ...target, label: m[2] });
    m = re.exec(text);
  }
  return out;
}

/**
 * A body cut into its plain runs and its mentions, in document order, for a surface that draws
 * each mention itself (a name, a title link) but does not render markdown.
 * @param {string} text
 * @returns {Array<{text: string}|{kind: 'account'|'item', id: string, label: string}>}
 */
export function splitMentions(text) {
  if (!text) return [];
  const out = [];
  const re = new RegExp(MENTION_RE.source, 'g');
  let at = 0;
  let m = re.exec(text);
  while (m) {
    const target = parseMentionRef(m[1]);
    // A mention with no usable id stays as the text it was written as.
    if (target) {
      if (m.index > at) out.push({ text: text.slice(at, m.index) });
      out.push({ ...target, label: m[2] });
      at = m.index + m[0].length;
    }
    m = re.exec(text);
  }
  if (at < text.length) out.push({ text: text.slice(at) });
  return out;
}

/** Deduplicated ids of one kind, in first-seen order. */
export function extractMentionIds(text, kind) {
  const seen = new Set();
  for (const mention of extractMentions(text)) {
    if (mention.kind === kind) seen.add(mention.id);
  }
  return [...seen];
}

/** Deduplicated account ids mentioned in `text`. */
export function extractMentionedAccountIds(text) {
  return extractMentionIds(text, 'account');
}

/** Deduplicated item ids mentioned in `text`. */
export function extractMentionedItemIds(text) {
  return extractMentionIds(text, 'item');
}

/**
 * Replace every stored `[[ref|label]]` with its display form `@label`.
 *
 * For surfaces that quote a body as plain text — the notification popup's snippet line — where a
 * pill cannot render and the storage form (mostly a uuid) must not leak to the reader.
 */
export function stripMentionSyntax(text) {
  if (!text) return '';
  return String(text).replace(new RegExp(MENTION_RE.source, 'g'), (_match, _ref, label) => `@${label}`);
}

/**
 * The text with every mention in its display form, as the reader may see it: a person as `@Name`,
 * anything else as `@` + its current name, or as "Something you can't see" when the reader may not
 * see it. For surfaces that quote a body as plain text (the notification bell). `answerFor(ref)` is
 * the reader's answer for one stored reference (src/data/mentionTargets.js) — undefined while it is
 * being fetched.
 */
export function mentionPlainText(text, answerFor) {
  if (!text) return '';
  return String(text).replace(new RegExp(MENTION_RE.source, 'g'), (whole, ref, label) => {
    const target = parseMentionRef(ref);
    if (!target) return whole;
    if (!isAudienceGatedKind(target.kind)) return `@${label}`;
    const shown = mentionDisplay(answerFor ? answerFor(String(ref).trim()) : null, label);
    return shown.state === 'visible' ? `@${shown.text}` : shown.text;
  });
}

/**
 * The start of a mention that runs off the end of the text: `[[`, then as much of a reference (and of
 * its label) as was kept. Written to the reference's own shape, so prose that merely holds `[[` and a
 * space ("type [[ for a link") is not taken for one.
 */
const CUT_MENTION_RE = /\[\[(?:@|(?:type|field|group):)?[0-9A-Za-z-]*(?:\\?\|[^[\]]*)?\]?$/;

/**
 * The text with a mention a cut went through taken off its end. A body shortened before its mentions
 * were read (`left(body, 200)` in SQL, a `.slice` on a raw description) can end inside a token,
 * `…and this [[@cd60835f-b066-4bd7`, which no matcher reads as a mention, so the half-uuid reached
 * the screen. Only an unclosed token at the very end is taken; whole mentions are left to be read.
 */
export function dropCutMention(text) {
  return String(text ?? '').replace(CUT_MENTION_RE, '').trimEnd();
}

/**
 * A snippet of a body, for any surface that quotes one as plain text: its mentions as words FIRST,
 * then the cut to `max` characters. In that order because cutting the stored text could land inside
 * a token and leave half a uuid behind; a body that arrives already cut has its broken token dropped.
 *
 * `answerFor` is mentionPlainText's (the reader's answers, src/data/mentionTargets.js). A surface with
 * no reader to ask for (an email, a calendar event, a public page) leaves it out, and every mention
 * reads with its stored label, as stripMentionSyntax writes it. PURE.
 */
export function mentionSnippet(text, { max = 0, firstLine = false, answerFor = null } = {}) {
  let t = dropCutMention(text);
  if (firstLine) t = t.split('\n')[0];
  t = (answerFor ? mentionPlainText(t, answerFor) : stripMentionSyntax(t)).trim();
  if (max && t.length > max) t = `${t.slice(0, max - 1).trimEnd()}…`;
  return t;
}

/**
 * A chat message as the thread draws it: every mention as "@Name". A chat's composer offers people
 * only (migration 20261210120000), and a person's name is not audience-gated, so this needs no
 * answer from the database; a mention of any other kind, which only a paste could bring, reads with
 * its stored label, exactly as it did before mentions reached chats. PURE.
 */
export function chatMessageWords(text) {
  return mentionPlainText(text, () => ({ visible: true, label: null }));
}

/** An opening (or closing) code fence: up to three spaces, then three or more backticks or tildes. */
const FENCE_RE = /^ {0,3}(`{3,}|~{3,})/;
/** An inline code span, or a mention whose separator is a bare pipe. Code spans are matched only to be skipped. */
const CODE_OR_BARE_MENTION_RE = /(`[^`\n]+`)|\[\[[^[\]|\\\n]+\|[^[\]\n]+\]\]/g;

/**
 * Escape the separator of every bare-pipe mention that sits on a table row: `[[id|Label]]` becomes
 * `[[id\|Label]]` on any line that also carries a pipe of its own.
 *
 * A GFM parser splits a row on every unescaped pipe BEFORE it looks for mentions, so a bare separator
 * cuts the mention into two dead cells and the row's last cell falls off the end, because a row keeps
 * only as many cells as its header has. Bodies written outside the editor (a script, an AI, a paste)
 * use the bare form everywhere. The read-only renderer survives that, since it lifts mentions out
 * before markdown sees the text, but the editor did not. One open and one save wrote the split back,
 * which is how "The Duplication Tax" lost a column from every table row naming an item (2026-10-02).
 *
 * Same table-row test as the vault importer (`lineHasStructuralPipe`): a pipe left on the line once
 * its mentions and code spans are masked out. Fenced code blocks and inline code are never touched.
 * Escaping on a non-table line that happens to carry a pipe is harmless: the editor's mention rule
 * reads `\|` as the separator wherever it appears.
 */
export function escapeTableRowMentionPipes(markdown) {
  const text = String(markdown ?? '');
  if (!text.includes('[[')) return text;
  let fence = null;
  return text.split('\n').map((line) => {
    const opener = line.match(FENCE_RE);
    if (fence) {
      if (opener && opener[1][0] === fence[0] && opener[1].length >= fence.length) fence = null;
      return line;
    }
    if (opener) { fence = opener[1]; return line; }
    if (!line.includes('[[')) return line;
    const masked = line.replace(/`[^`\n]+`/g, '').replace(new RegExp(MENTION_RE.source, 'g'), '');
    if (!/(^|[^\\])\|/.test(masked)) return line;
    return line.replace(CODE_OR_BARE_MENTION_RE, (whole, code) => (code !== undefined ? whole : whole.replace('|', '\\|')));
  }).join('\n');
}

/**
 * The text with every mention of the given accounts turned back into plain words: `[[@id|Ana]]`
 * becomes `Ana`. This is what "untag" means when a writer names someone who cannot open the item and
 * chooses not to share it (useMentionAccessGate): the name stays in the sentence, nobody is notified,
 * and the note is not given to them. Item mentions and other people's mentions are left alone.
 */
export function untagAccounts(text, accountIds) {
  const body = String(text ?? '');
  if (!body.includes('[[') || !accountIds?.length) return body;
  const ids = new Set(accountIds);
  return body.replace(new RegExp(MENTION_RE.source, 'g'), (whole, ref, label) => {
    const target = parseMentionRef(ref);
    return target?.kind === 'account' && ids.has(target.id) ? label : whole;
  });
}

/** How many times each account is named in `text`. */
export function accountMentionCounts(text) {
  const counts = new Map();
  for (const mention of extractMentions(text)) {
    if (mention.kind === 'account') counts.set(mention.id, (counts.get(mention.id) || 0) + 1);
  }
  return counts;
}

/**
 * Account ids named MORE TIMES in `next` than in `prev` — a new mention of them, whether or not the
 * text already named them somewhere.
 *
 * It counts rather than comparing who is named, because a second "@Vincent, one more thing" on a
 * page that already names him is a new mention, and a set of names cannot see it. The server re-arms
 * a page mention's notification when it hears one (migration 20261117160000), so the person is told
 * again. Renaming a mention's label, or deleting one and typing another between two saves, leaves the
 * count where it was and announces nothing.
 *
 * Every mention surface saves repeatedly while you type, so the caller diffs against the last saved
 * text: the common save carries an empty list and skips the round-trip entirely.
 */
export function newlyMentionedAccountIds(prevText, nextText) {
  const before = accountMentionCounts(prevText);
  const out = [];
  for (const [id, n] of accountMentionCounts(nextText)) {
    if (n > (before.get(id) || 0)) out.push(id);
  }
  return out;
}
